import type { ReactNode } from 'react';

export type PillTone = 'neutral' | 'info' | 'positive' | 'warning' | 'critical';
export type PillSize = 'sm' | 'md' | 'lg';

export interface PillProps {
  /** Visible content of the pill (string or inline node). */
  children: ReactNode;
  /** Semantic tone — drives background/text colors. Defaults to `neutral`. */
  tone?: PillTone;
  /** Density: `sm` for inline metadata, `md` for standalone rows. */
  size?: PillSize;
  /** Optional leading icon (e.g. a UnifiedIcon). Hidden from AT by default. */
  icon?: ReactNode;
  /** Tooltip / native title — useful when the pill value lacks an inline label. */
  title?: string;
  /** Accessible label override; defaults to nothing extra (pill text suffices). */
  ariaLabel?: string;
  /** Extra Tailwind utilities. */
  className?: string;
  /** Test hook. */
  'data-testid'?: string;
}

const TONE: Record<PillTone, string> = {
  neutral:
    'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200',
  info:
    'bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-200',
  positive:
    'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-200',
  warning:
    'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200',
  critical:
    'bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-200',
};

const SIZE: Record<PillSize, string> = {
  sm: 'text-[11px] leading-none px-2 py-1',
  md: 'text-xs leading-none px-2.5 py-1.5',
  lg: 'text-base leading-none font-bold px-3.5 py-2',
};

/**
 * Compact, rounded "pill" badge for inline metadata such as level, area, tags.
 * Light + dark safe; pure presentational primitive (no routing, no state).
 *
 * Example:
 * ```tsx
 * <Pill tone="info" title="Level">L3</Pill>
 * ```
 */
export function Pill({
  children,
  tone = 'neutral',
  size = 'sm',
  icon = null,
  title,
  ariaLabel,
  className = '',
  'data-testid': dataTestId,
}: PillProps) {
  return (
    <span
      title={title}
      aria-label={ariaLabel}
      data-testid={dataTestId}
      className={[
        'inline-flex max-w-full items-center gap-1 rounded-full font-medium whitespace-nowrap',
        TONE[tone],
        SIZE[size],
        className,
      ].join(' ')}
    >
      {icon ? (
        <span aria-hidden className="inline-flex shrink-0">
          {icon}
        </span>
      ) : null}
      <span className="truncate">{children}</span>
    </span>
  );
}
